import type { SlaExtraErrorAttributes, SlaViewMode } from '../../common/types.tsx';

type JsonFieldResponse<T> = {
	fieldId: string;
	value: T;
	isEditable: boolean;
};

type CycleTimeData = {
	friendly: string;
	epochMillis: number;
};

type CycleTimeDataSimple = {
	millis: number;
	friendly: string;
};

type CycleData = {
	startTime: CycleTimeData;
	goalDuration: CycleTimeDataSimple;
	elapsedTime: CycleTimeDataSimple;
	remainingTime: CycleTimeDataSimple;
	remainingTimeUnits?: string;
	breached: boolean;
};

export type CompletedCycle = CycleData & {
	breachTime?: CycleTimeData;
	stopTime: CycleTimeData;
};

export type OngoingCycle = CycleData & {
	breachTime?: CycleTimeData;
	paused: boolean;
	withinCalendarHours: boolean;
};

type Sla = {
	id: string;
	name: string;
	_links: {
		self: string;
	};
	completedCycles: CompletedCycle[];
	ongoingCycle?: OngoingCycle;
	slaDisplayFormat?: SLA_DISPLAY_FORMAT;
};

type SlaFieldResponse = JsonFieldResponse<Sla | SlaError>;
type SLA_DISPLAY_FORMAT = 'OLD_SLA_FORMAT' | 'NEW_SLA_FORMAT';

export type SlaProps = {
	isBreached: boolean;
	timeRemainingFriendly: string;
	status: SlaStatus;
	timeRemainingInMinutes: number;
	breachTime: string | undefined;
	elapsedTime: string;
	goalDuration: string;
	withinCalendarHours: boolean | undefined;
	hoverContent?: string | undefined;
	slaDisplayFormat?: SLA_DISPLAY_FORMAT;
};

export type SlaError = {
	errorMessage: string;
	i18nErrorMessage: {
		i18nKey: string;
		parameters: string[];
	};
};

export type Props = {
	viewMode: SlaViewMode;
	value?: SlaProps | SlaError;
	onError?: (location: string, error: Error, attributes: SlaExtraErrorAttributes) => void;
	isLastColumn?: boolean;
};

export type SlaResponse = SlaFieldResponse;
type StoredFieldUpdater<I> = (newVal: Partial<I>, oldVal: I) => void;
export type GenericFieldDataTransformerInput<I> = {
	storedValue: I;
	storedFieldUpdater: StoredFieldUpdater<I>;
};

export const ONGOING = 'ONGOING' as const;
export const COMPLETED = 'COMPLETED' as const;
export const PAUSED = 'PAUSED' as const;
export type SlaStatus = typeof ONGOING | typeof COMPLETED | typeof PAUSED;

export const SLA_CUSTOM_FIELD_TYPE = 'com.atlassian.servicedesk:sd-sla-field' as const;

export type SlaDataSelectorProps = {
	fieldType: typeof SLA_CUSTOM_FIELD_TYPE;
	value?: SlaProps | SlaError;
};

type FieldDataTransformers = {
	['com.atlassian.servicedesk:sd-sla-field']: (
		arg1: GenericFieldDataTransformerInput<SlaFieldResponse>,
	) => SlaDataSelectorProps;
};

type TransformableFieldType = keyof FieldDataTransformers;

export type FieldDataTransformer<T extends TransformableFieldType> = FieldDataTransformers[T];
